import { useState } from 'react';
import { Button, ConfirmDialog, EmptyState, PageHeader } from '@/shared/components';
import type { InvoiceTemplateResponse } from '@/shared/api';
import { InvoiceItemPresetsCard } from './invoice-presets/InvoiceItemPresetsCard';
import { InvoiceTemplateCard } from './invoice-templates/InvoiceTemplateCard';
import { InvoiceTemplateEditor } from './invoice-templates/InvoiceTemplateEditor';
import { useInvoiceTemplates } from './invoice-templates/useInvoiceTemplates';

type EditorTarget = { mode: 'edit'; template: InvoiceTemplateResponse } | { mode: 'create' };

/**
 * Rechnungsvorlagen eines Workspace: Absenderdaten, Bankverbindung, Logo — dazu der
 * Positionskatalog als zweiter Abschnitt.
 *
 * <p>Die Sprache folgt Block C: die per `isPrimary` markierte Vorlage heißt in der Oberfläche
 * durchgehend „Standardvorlage des Workspace", nicht „Primary".</p>
 */
export function InvoiceTemplatesPage() {
  const catalogue = useInvoiceTemplates();
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [deleting, setDeleting] = useState<InvoiceTemplateResponse | null>(null);
  // Der Ladezustand gehört an die Zeile, nicht an die Mutation.
  const [busyId, setBusyId] = useState<string | null>(null);

  if (editor) {
    return (
      <InvoiceTemplateEditor
        template={editor.mode === 'edit' ? editor.template : null}
        onCancel={() => setEditor(null)}
        onSaved={() => {
          setEditor(null);
          catalogue.invalidate();
        }}
      />
    );
  }

  if (catalogue.isLoading)
    return <p className="text-ink-soft text-sm">Rechnungsvorlagen werden geladen…</p>;
  if (catalogue.hasError || !catalogue.templates)
    return <EmptyState icon="Error" title="Konnte Rechnungsvorlagen nicht laden" />;

  const templates = catalogue.templates;

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Vorlagen für"
        titleAccent="Rechnungen"
        description="Absenderdaten, Bankverbindung und Logo Ihrer Rechnungen. Die Standardvorlage des Workspace gilt überall dort, wo am Apartment nichts anderes hinterlegt ist."
        actions={
          <Button variant="brand" onClick={() => setEditor({ mode: 'create' })}>
            Neue Vorlage
          </Button>
        }
      />

      {templates.length === 0 ? (
        <EmptyState
          icon="Info"
          title="Noch keine Rechnungsvorlage"
          description="Legen Sie die erste an — sie wird automatisch die Standardvorlage des Workspace."
          action={{
            label: 'Neue Vorlage',
            onClick: () => setEditor({ mode: 'create' }),
            variant: 'brand',
          }}
        />
      ) : (
        <div className="grid gap-3">
          {templates.map((template) => (
            <InvoiceTemplateCard
              key={template.id}
              template={template}
              busy={busyId === template.id}
              onSetPrimary={() => {
                setBusyId(template.id);
                catalogue.setPrimary.mutate(template.id, { onSettled: () => setBusyId(null) });
              }}
              onEdit={() => setEditor({ mode: 'edit', template })}
              onDelete={() => setDeleting(template)}
            />
          ))}
        </div>
      )}

      <div className="mt-8">
        <InvoiceItemPresetsCard />
      </div>

      {/* Vorher ein natives `confirm()` — es steht ausserhalb der Marke, ist auf Mobile nicht
          gestaltbar und nennt den Namen nur im Fliesstext. */}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title="Rechnungsvorlage löschen?"
        description={
          deleting
            ? `„${deleting.name}" verschwindet aus der Auswahl. Bereits ausgestellte Rechnungen bleiben unverändert — sie tragen ihre Absenderdaten selbst.`
            : ''
        }
        confirmLabel="Löschen"
        loading={catalogue.remove.isPending}
        onConfirm={() => {
          if (deleting) {
            catalogue.remove.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
      />
    </>
  );
}
